import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { trialService } from '../../services/trialService';
import toast from 'react-hot-toast';

interface Subscription {
  id: string;
  status: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  plan_name: string;
  price: string;
  interval: string;
}

const AccountPage: React.FC = () => {
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [trialStatus, setTrialStatus] = useState<{
    isInTrial: boolean;
    daysRemaining: number;
    trialEnd: string | null;
  }>({ isInTrial: false, daysRemaining: 0, trialEnd: null });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    } else if (user) {
      // Get user profile info
      fetchUserProfile();
      
      // Get subscription info
      fetchSubscriptionDetails();
      
      // Get trial status info
      fetchTrialStatus();
    }
  }, [isAuthenticated, user, navigate]);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        console.log('No user ID available, using email from auth context');
        // If we have a user email from auth context but no profile, use that
        if (user?.email) {
          setEmail(user.email);
          return;
        }
        return;
      }
      
      // Improved Supabase query with better error checking
      console.log('Fetching user profile with ID:', user.id);
      
      const { data, error } = await supabase
        .from('users')
        .select('name, email')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Supabase query error details:', error);
        // Fallback to auth context data if query fails
        if (user?.email) {
          setEmail(user.email);
        }
        throw error;
      }
      
      if (data) {
        console.log('User profile data retrieved successfully');
        setName(data.name || '');
        setEmail(data.email || user.email || '');
      } else {
        console.log('No user profile data found, using email from auth context');
        if (user?.email) {
          setEmail(user.email);
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      toast.error('Unable to load profile data, but you can still use the application');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      if (!user?.id) return;
      
      const token = await getToken();
      if (!token) return;
      
      const status = await trialService.getTrialStatus(token);
      setTrialStatus({
        isInTrial: status.isInTrial,
        daysRemaining: status.daysRemaining,
        trialEnd: status.trialEnd
      });
    } catch (error) {
      console.error('Error fetching trial status:', error);
      // We don't show error toast for trial failures to avoid confusion
    }
  };

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      if (!user?.id) {
        console.log('No user ID available for subscription fetch');
        return;
      }
      
      console.log('Fetching subscription details for user:', user.id);
      
      // Call backend API to get subscription details with enhanced error handling
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL(`/api/payment/subscription/${user.id}`, process.env.REACT_APP_API_URL || 'http://localhost:5000');
      
      try {
        const token = await getToken();
        if (!token) {
          console.error('No auth token available for subscription fetch');
          return;
        }
        
        const response = await fetch(baseUrl.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        
        // Check for specific error codes and handle appropriately
        if (response.status === 404) {
          console.log('No subscription found for user - normal for new users');
          return; // No subscription is a valid state, not an error
        } else if (response.status === 401 || response.status === 403) {
          console.error('Authentication error when fetching subscription');
          return;
        } else if (!response.ok) {
          console.error(`Subscription fetch failed with status: ${response.status}`);
          throw new Error(`Failed to fetch subscription: ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log('Subscription data retrieved successfully');
        
        // Validate subscription data before using it
        if (data.subscription) {
          // Check required fields exist
          if (!data.subscription.id || !data.subscription.status) {
            console.error('Incomplete subscription data received', data.subscription);
            return;
          }
          
          setSubscription({
            id: data.subscription.id,
            status: data.subscription.status,
            current_period_end: new Date(data.subscription.current_period_end * 1000).toLocaleDateString(),
            cancel_at_period_end: data.subscription.cancel_at_period_end || false,
            plan_name: data.subscription.plan?.name || 'Unknown Plan',
            price: formatPrice(data.subscription.plan?.amount || 0, data.subscription.plan?.currency || 'GBP'),
            interval: data.subscription.plan?.interval || 'month'
          });
        }
      } catch (fetchError) {
        console.error('Network error when fetching subscription:', fetchError);
        // Handle network errors separately from response errors
      }
    } catch (error) {
      console.error('Error in subscription fetch logic:', error);
      // Only show an error toast if this is an unexpected error, not for missing subscriptions
      // Don't show error toast as user might not have a subscription yet
    } finally {
      setLoading(false);
    }
  };

  const getToken = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  };

  const formatPrice = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
    });
    return formatter.format(amount / 100);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (!user?.id) return;
      
      const { error } = await supabase
        .from('users')
        .update({ name })
        .eq('id', user.id);
      
      if (error) throw error;
      
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      if (!user?.id || !subscription?.id) return;
      
      // Call backend API to cancel subscription
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/subscription/cancel', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }
      
      // Update subscription state
      setSubscription({
        ...subscription,
        cancel_at_period_end: true
      });
      
      toast.success('Your subscription will be canceled at the end of the current billing period');
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsCancelling(true);
      if (!user?.id || !subscription?.id) return;
      
      // Call backend API to reactivate subscription
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/subscription/reactivate', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          subscriptionId: subscription.id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to reactivate subscription');
      }
      
      // Update subscription state
      setSubscription({
        ...subscription,
        cancel_at_period_end: false
      });
      
      toast.success('Your subscription has been reactivated');
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error('Failed to reactivate subscription');
    } finally {
      setIsCancelling(false);
    }
  };
  
  // Handler for account upgrade using guest checkout endpoint
  const handleUpgradeAccount = async (planId: string) => {
    try {
      setIsUpgrading(true);
      if (!user?.id || !email) return;
      
      // Use the guest checkout endpoint to create a checkout session
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/guest-checkout', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          email: email, // Use the user's existing email
          planId: planId // 'monthly' or 'annual'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error('Failed to start upgrade process. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setLoading(true);
      if (!user?.id) return;
      
      // Call backend API to create a billing portal session
      // Create a proper URL instance to ensure correct formatting
      const baseUrl = new URL('/api/payment/create-portal-session', process.env.REACT_APP_API_URL || 'http://localhost:5000');
      const response = await fetch(baseUrl.toString(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getToken()}`
        },
        body: JSON.stringify({
          returnUrl: window.location.href
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }
      
      const { url } = await response.json();
      
      // Redirect to Stripe billing portal
      window.location.href = url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error('Failed to access billing portal');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = () => {
    navigate('/reset-password');
  };

  const handleAccountDeletion = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        setLoading(true);
        
        // Cancel subscription if active
        if (subscription && subscription.status === 'active' && !subscription.cancel_at_period_end) {
          await handleCancelSubscription();
        }
        
        // Delete user from Supabase
        const { error } = await supabase.auth.admin.deleteUser(user?.id || '');
        
        if (error) throw error;
        
        // Sign out after deletion
        await signOut();
        
        toast.success('Your account has been deleted');
        navigate('/');
      } catch (error) {
        console.error('Error deleting account:', error);
        toast.error('Failed to delete account');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Trial Banner - only shown when user is in trial */}
      {subscription && subscription.status === 'trialing' && trialStatus.isInTrial && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-md text-white">
          <div className="container mx-auto max-w-5xl">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-bold">Your trial ends in {trialStatus.daysRemaining} days!</h3>
                <p className="mt-1">Your card will be automatically charged on {subscription.current_period_end}.</p>
              </div>
              <div className="flex space-x-4">
                <button 
                  onClick={() => navigate('/payment')} 
                  className="px-6 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all"
                >
                  Manage Subscription
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="container mx-auto max-w-5xl">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">My Account</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage your account details and subscription.</p>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('profile')}
                className={`${activeTab === 'profile' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('subscription')}
                className={`${activeTab === 'subscription' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Subscription
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`${activeTab === 'security' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Security
              </button>
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">Full name</label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email address</label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm bg-gray-50 sm:text-sm p-2"
                    />
                    <p className="mt-1 text-sm text-gray-500">Your email cannot be changed</p>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loading}
                      className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </form>
            )}
            
            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="space-y-6">
                {subscription ? (
                  <>
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Current Plan</h4>
                          <p className="mt-1 text-lg font-semibold">{subscription.plan_name}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Status</h4>
                          <div className="mt-1 flex items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              subscription.status === 'active' ? 'bg-green-100 text-green-800' : 
                              subscription.status === 'trialing' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {subscription.status === 'active' ? 'Active' : 
                               subscription.status === 'trialing' ? `Trial (${trialStatus.daysRemaining} days left)` : 
                               subscription.status}
                            </span>
                            {subscription.cancel_at_period_end && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Cancels at period end
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Price</h4>
                          <p className="mt-1 text-lg font-semibold">{subscription.price} / {subscription.interval}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Current Period Ends</h4>
                          <p className="mt-1">{subscription.current_period_end}</p>
                          {subscription.status === 'trialing' && trialStatus.isInTrial && (
                            <p className="mt-1 text-xs text-rose-600 font-medium">
                              Your card will be automatically charged on this date unless you cancel before the trial ends.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                      <button
                        type="button"
                        onClick={handleUpdatePaymentMethod}
                        disabled={loading}
                        className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Update Payment Method
                      </button>
                      
                      {subscription.status === 'active' && !subscription.cancel_at_period_end ? (
                        <button
                          type="button"
                          onClick={handleCancelSubscription}
                          disabled={isCancelling}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          {isCancelling ? 'Processing...' : 'Cancel Subscription'}
                        </button>
                      ) : subscription.status === 'active' && subscription.cancel_at_period_end ? (
                        <button
                          type="button"
                          onClick={handleReactivateSubscription}
                          disabled={isCancelling}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          {isCancelling ? 'Processing...' : 'Reactivate Subscription'}
                        </button>
                      ) : null}
                    </div>
                    
                    <div className="mt-4 border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-3">Subscription History</h4>
                      <p className="text-sm text-gray-500">
                        To view your complete billing history, update payment methods, or download invoices, 
                        use the Stripe Customer Portal.
                      </p>
                      <button
                        type="button"
                        onClick={handleUpdatePaymentMethod}
                        className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Billing History
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="py-6">
                    <div className="text-center mb-8">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No active subscription</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        You don't have an active subscription with us yet.
                      </p>
                    </div>
                    
                    {/* Upgrade Account Section */}
                    <div className="bg-white border rounded-lg shadow-sm p-6 max-w-md mx-auto">
                      <h4 className="text-lg font-medium text-gray-900 mb-3">Upgrade Your Account</h4>
                      
                      <div className="space-y-4 mb-6">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Unlimited business idea generation</p>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Complete business plan creation</p>
                        </div>
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <p className="ml-3 text-sm text-gray-700">Premium features and priority support</p>
                        </div>
                      </div>
                      
                      <div className="mt-2 space-y-3">
                        <button
                          type="button"
                          onClick={() => handleUpgradeAccount('monthly')}
                          disabled={isUpgrading}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isUpgrading ? 'Processing...' : 'Upgrade Monthly - £19/month'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUpgradeAccount('annual')}
                          disabled={isUpgrading}
                          className={`w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${isUpgrading ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                          {isUpgrading ? 'Processing...' : 'Upgrade Annually - £182.40/year'} {!isUpgrading && <span className="text-xs ml-1 bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">Save 20%</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h4 className="text-base font-medium text-gray-900">Change Password</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Update your password to keep your account secure.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleChangePassword}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Change Password
                    </button>
                  </div>
                </div>
                
                <div className="pt-5 border-t border-gray-200">
                  <h4 className="text-base font-medium text-gray-900">Delete Account</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleAccountDeletion}
                      className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
